import {
  ApiError,
  backendFetchResponse,
  parseResponse,
} from "@softmax/observatory-client/transport";
import {
  replaySessionSchema,
  type ReplayLaunchIdentity,
  type ReplaySession,
  type ReplaySessionResult,
} from "./replay-session-records";

export async function createReplaySession({
  coworldId,
  replayUri,
  elevatedPrivileges,
  signal,
}: ReplayLaunchIdentity &
  Readonly<{
    signal: AbortSignal;
  }>): Promise<ReplaySessionResult> {
  signal.throwIfAborted();
  const localViewer = localReplayViewer({ coworldId, replayUri });

  if (localViewer)
    return { ...localViewer, responseReceivedAt: performance.now() };

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (elevatedPrivileges) headers["X-Use-Elevated-Privileges"] = "true";

  const response = await backendFetchResponse(
    "",
    "/api/observatory/v2/coworlds/replays/session",
    {
      method: "POST",
      headers,
      signal,
      body: JSON.stringify({ coworld_id: coworldId, replay_uri: replayUri }),
    },
  );
  const responseReceivedAt = performance.now();

  if (!response.ok) {
    if (
      response.headers
        .get("content-type")
        ?.toLowerCase()
        .includes("application/json")
    )
      return parseResponse<never>(response);

    throw new ApiError(
      (await response.text()) || "Replay session failed to start",
      response.status,
      undefined,
      undefined,
      response,
    );
  }

  const session = replaySessionSchema.parse(await response.json());
  void new URL(session.viewer_url);
  signal.throwIfAborted();
  return { ...session, responseReceivedAt };
}

function localReplayViewer({
  coworldId,
  replayUri,
}: Pick<
  ReplayLaunchIdentity,
  "coworldId" | "replayUri"
>): ReplaySession | null {
  if (process.env.NODE_ENV === "production") return null;

  const bundleUrl = process.env.NEXT_PUBLIC_COWORLD_REPLAY_VIEWER_BUNDLE_URL;
  const overrideCoworldId =
    process.env.NEXT_PUBLIC_COWORLD_REPLAY_VIEWER_COWORLD_ID;

  if (!bundleUrl || overrideCoworldId !== coworldId) return null;

  const viewerUrl = new URL(
    "index.html",
    bundleUrl.endsWith("/") ? bundleUrl : `${bundleUrl}/`,
  );
  viewerUrl.hash = new URLSearchParams({ replay: replayUri }).toString();
  return { viewer_url: viewerUrl.toString(), ready: true };
}
